import React, { Component } from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";

class SEO extends Component {
  static propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
    keywords: PropTypes.string,
    image: PropTypes.string,
    url: PropTypes.string,
    type: PropTypes.string,
    article: PropTypes.object,
    product: PropTypes.object,
    breadcrumbs: PropTypes.array,
    structuredData: PropTypes.object,
    noindex: PropTypes.bool,
    nofollow: PropTypes.bool,
    canonical: PropTypes.string,
  };

  static defaultProps = {
    type: "website",
    noindex: false,
    nofollow: false,
  };

  generateTitle = () => {
    const { title, setting } = this.props;
    const siteName = (setting && setting.setting && setting.setting.title) || "Botaniksan";
    
    if (!title) {
      return `${siteName} - Doğal Ürünler ve Bitkisel Çözümler`;
    }
    
    return title.includes(siteName) ? title : `${title} | ${siteName}`;
  };

  generateDescription = () => {
    const { description, setting } = this.props;
    const defaultDescription = (setting && setting.setting && setting.setting.meta_description) || 
      "Botaniksan'da doğal ve organik ürünler, bitkisel çözümler ve sağlıklı yaşam ürünleri. Kaliteli ve güvenilir doğal ürünler için doğru adres.";
    
    return description || defaultDescription;
  };

  generateKeywords = () => {
    const { keywords, setting } = this.props;
    const defaultKeywords = (setting && setting.setting && setting.setting.meta_keywords) || 
      "botaniksan, doğal ürünler, organik, bitkisel, sağlıklı yaşam, doğal çözümler";
    
    return keywords || defaultKeywords;
  };

  generateImageUrl = () => {
    const { image, setting } = this.props;
    const baseUrl = process.env.NODE_ENV === "development" 
      ? "http://localhost:4040" 
      : "https://botaniksan.com";
    
    if (image) {
      return image.startsWith('http') ? image : `${baseUrl}${image}`;
    }
    
    // Default og image
    return `${baseUrl}/images/logo.png`;
  };

  generateUrl = () => {
    const { url } = this.props;
    const baseUrl = process.env.NODE_ENV === "development" 
      ? "http://localhost:3000" 
      : "https://botaniksan.com";
    
    if (url) {
      return url.startsWith('http') ? url : `${baseUrl}${url}`;
    }
    
    return `${baseUrl}${window.location.pathname}`;
  };

  generateRobotsContent = () => {
    const { noindex, nofollow } = this.props;
    const robots = [];
    
    if (noindex) robots.push("noindex");
    else robots.push("index");
    
    if (nofollow) robots.push("nofollow");
    else robots.push("follow");
    
    return robots.join(", ");
  };

  generateStructuredData = () => {
    const { structuredData, product, article, breadcrumbs, setting } = this.props;
    
    if (structuredData) {
      return JSON.stringify(structuredData);
    }

    const baseUrl = process.env.NODE_ENV === "development" 
      ? "https://localhost:3000" 
      : "https://botaniksan.com";
    
    const data = [];

    // Organization markup
    data.push({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: (setting && setting.setting && setting.setting.title) || "Botaniksan",
      url: baseUrl,
      logo: `${baseUrl}/images/logo.png`,
      contactPoint: {
        "@type": "ContactPoint",
        telephone: (setting && setting.setting && setting.setting.phone) || "+90-XXX-XXX-XXXX",
        contactType: "customer service",
        areaServed: "TR",
        availableLanguage: "Turkish"
      },
      sameAs: [
        "https://www.facebook.com/botaniksan",
        "https://www.instagram.com/botaniksan"
      ]
    });

    // Website markup
    data.push({
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: (setting && setting.setting && setting.setting.title) || "Botaniksan",
      url: baseUrl,
      potentialAction: {
        "@type": "SearchAction",
        target: `${baseUrl}/arama?q={search_term_string}`,
        "query-input": "required name=search_term_string"
      }
    });

    // Product markup
    if (product) {
      data.push({
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.name,
        image: this.generateImageUrl(),
        description: product.meta_description || (product.content && product.content.replace(/<[^>]*>/g, '').slice(0, 160)) || '',
        sku: product.sku || product.id,
        brand: {
          "@type": "Brand",
          name: (setting && setting.setting && setting.setting.title) || "Botaniksan"
        },
        offers: {
          "@type": "Offer",
          url: this.generateUrl(),
          priceCurrency: "TRY",
          price: product.sale_price || product.price,
          availability: product.stock > 0 
            ? "https://schema.org/InStock" 
            : "https://schema.org/OutOfStock",
          seller: {
            "@type": "Organization",
            name: (setting && setting.setting && setting.setting.title) || "Botaniksan"
          }
        }
      });
    }

    // Article markup
    if (article) {
      data.push({
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.title,
        image: this.generateImageUrl(),
        datePublished: article.created_at,
        dateModified: article.updated_at || article.created_at,
        author: {
          "@type": "Organization",
          name: (setting && setting.setting && setting.setting.title) || "Botaniksan"
        },
        publisher: {
          "@type": "Organization",
          name: (setting && setting.setting && setting.setting.title) || "Botaniksan",
          logo: {
            "@type": "ImageObject",
            url: `${baseUrl}/images/logo.png`
          }
        },
        description: article.meta_description || (article.content && article.content.replace(/<[^>]*>/g, '').slice(0, 160)) || ''
      });
    }

    // Breadcrumb markup
    if (breadcrumbs && breadcrumbs.length > 0) {
      data.push({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((breadcrumb, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: breadcrumb.name,
          item: breadcrumb.url
        }))
      });
    }

    return JSON.stringify(data);
  };

  render() {
    const title = this.generateTitle();
    const description = this.generateDescription();
    const keywords = this.generateKeywords();
    const imageUrl = this.generateImageUrl();
    const url = this.generateUrl();
    const robotsContent = this.generateRobotsContent();
    const structuredData = this.generateStructuredData();
    const { canonical, type } = this.props;

    return (
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta name="robots" content={robotsContent} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={canonical || url} />
        
        {/* Open Graph Meta Tags */}
        <meta property="og:type" content={type} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:url" content={url} />
        <meta property="og:site_name" content={(this.props.setting && this.props.setting.setting && this.props.setting.setting.title) || "Botaniksan"} />
        <meta property="og:locale" content="tr_TR" />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={imageUrl} />
        
        {/* Additional Meta Tags */}
        <meta name="author" content={(this.props.setting && this.props.setting.setting && this.props.setting.setting.title) || "Botaniksan"} />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta httpEquiv="Content-Language" content="tr" />
        <meta name="geo.region" content="TR" />
        <meta name="geo.country" content="Turkey" />
        
        {/* Structured Data */}
        {structuredData && (
          <script type="application/ld+json">
            {structuredData}
          </script>
        )}
      </Helmet>
    );
  }
}

const mapStateToProps = ({ setting }) => ({
  setting
});

export default connect(mapStateToProps)(SEO);


// WEBPACK FOOTER //
// ./src/components/shared/SEO.js